import { normalizeChipCode } from './chip-code';

/**
 * Parsing e mappatura dei file di import chip.
 *
 * Qui vive tutta la logica che decide *cosa* verrà importato: riconoscimento
 * delle intestazioni, normalizzazione delle celle, individuazione di duplicati e
 * righe senza codice. È pura di proposito — la lettura del file (xlsx/csv) e la
 * scrittura sul database stanno altrove, così questa parte è testabile senza
 * toccare né il disco né la rete.
 */

export const CHIP_FIELDS = [
  'chipCode',
  'team',
  'firstName',
  'lastName',
  'category',
  'uciId',
  'raceNumber',
  'race',
] as const;

export type ChipField = (typeof CHIP_FIELDS)[number];

/** Etichette mostrate nello step di mappatura colonne. */
export const CHIP_FIELD_LABELS: Record<ChipField, string> = {
  chipCode: 'Codice chip',
  team: 'Squadra',
  firstName: 'Nome',
  lastName: 'Cognome',
  category: 'Categoria',
  uciId: 'UCI ID',
  raceNumber: 'N. gara',
  race: 'Gara',
};

/**
 * Alias accettati per ogni campo, già normalizzati.
 *
 * Il file di esempio usa le intestazioni inglesi (`team_name`, `chip_code`, …);
 * gli alias italiani e le abbreviazioni coprono i file che arrivano dai diversi
 * software di cronometraggio, dove le intestazioni cambiano di gara in gara.
 */
const HEADER_ALIASES: Record<ChipField, readonly string[]> = {
  chipCode: [
    'chipcode', 'chip', 'code', 'codice', 'codicechip', 'chipid', 'idchip',
    'transponder', 'transpondercode', 'transponderid', 'tag', 'tagid',
  ],
  team: ['teamname', 'team', 'squadra', 'societa', 'nomesquadra', 'club', 'asd', 'ssd'],
  firstName: ['firstname', 'nome', 'nomeatleta'],
  lastName: ['lastname', 'surname', 'cognome', 'cognomeatleta'],
  category: ['category', 'categoria', 'cat', 'fascia'],
  uciId: ['uciid', 'uci', 'iduci', 'licenza', 'numerolicenza', 'codiceuci'],
  raceNumber: [
    'racenumber', 'numerogara', 'numgara', 'ngara', 'n gara', 'pettorale',
    'numeropettorale', 'bib', 'numero',
  ],
  race: ['race', 'gara', 'corsa', 'prova', 'tipogara'],
};

/** Campo obbligatorio: senza codice una riga non è importabile. */
export const REQUIRED_CHIP_FIELD: ChipField = 'chipCode';

/**
 * Normalizza un'intestazione per il confronto: minuscolo, senza accenti e senza
 * spazi o simboli. `"Chip_Code "` e `"CHIP CODE"` diventano entrambi `chipcode`.
 */
export function normalizeHeader(raw: string): string {
  if (typeof raw !== 'string') return '';
  return raw
    .normalize('NFD')
    .replace(/[̀-ͯ]/g, '')
    .toLowerCase()
    .replace(/[^a-z0-9]/g, '');
}

export type ColumnMapping = Record<ChipField, number | null>;

export const EMPTY_MAPPING: ColumnMapping = {
  chipCode: null,
  team: null,
  firstName: null,
  lastName: null,
  category: null,
  uciId: null,
  raceNumber: null,
  race: null,
};

/**
 * Associa automaticamente le colonne del file ai campi chip.
 *
 * Prima passata: corrispondenza esatta con un alias. Seconda passata, solo per i
 * campi rimasti vuoti: l'intestazione *contiene* un alias, così funzionano anche
 * intestazioni sporche tipo `"chip_code (obbligatorio)"`.
 *
 * Nella seconda passata si accettano solo alias di almeno 5 caratteri: sigle
 * corte come `nome` o `gara` sono troppo ambigue per una corrispondenza
 * parziale (`cognome` contiene `nome`).
 *
 * Una colonna non viene mai assegnata a due campi.
 */
export function autoMapHeaders(headers: readonly string[]): ColumnMapping {
  const normalized = headers.map(normalizeHeader);
  const mapping: ColumnMapping = { ...EMPTY_MAPPING };
  const taken = new Set<number>();

  for (const field of CHIP_FIELDS) {
    const index = normalized.findIndex(
      (header, i) => !taken.has(i) && header !== '' && HEADER_ALIASES[field].includes(header),
    );
    if (index !== -1) {
      mapping[field] = index;
      taken.add(index);
    }
  }

  for (const field of CHIP_FIELDS) {
    if (mapping[field] !== null) continue;
    const index = normalized.findIndex((header, i) => {
      if (taken.has(i) || header === '') return false;
      return HEADER_ALIASES[field].some((alias) => alias.length >= 5 && header.includes(alias));
    });
    if (index !== -1) {
      mapping[field] = index;
      taken.add(index);
    }
  }

  return mapping;
}

export type ImportRowStatus =
  | 'ok'
  | 'conflict'
  | 'duplicate_in_file'
  | 'duplicate_in_event'
  | 'missing_code';

/**
 * L'anagrafica di un chip già in elenco, ridotta ai campi che un file può
 * portare di diverso. Il codice fa da chiave e non compare qui.
 */
export type ChipComparable = {
  team: string | null;
  firstName: string | null;
  lastName: string | null;
  category: string | null;
  raceNumber: string | null;
  uciId: string | null;
  race: string | null;
};

/** I codici già in elenco, con i loro dati: `codice → anagrafica`. */
export type ExistingChipMap = ReadonlyMap<string, ChipComparable>;

/** I campi anagrafici su cui un file può entrare in conflitto con l'esistente. */
export const COMPARABLE_FIELDS = [
  'team',
  'firstName',
  'lastName',
  'category',
  'uciId',
  'raceNumber',
  'race',
] as const satisfies readonly ChipField[];

export type ImportRow = {
  /** Indice della riga dati nel file (0-based, intestazione esclusa). */
  rowIndex: number;
  status: ImportRowStatus;
  /** Codice normalizzato; stringa vuota se la riga non ne ha uno. */
  code: string;
  /** Codice così com'era nel file, per mostrarlo nell'anteprima. */
  rawCode: string;
  team: string | null;
  firstName: string | null;
  lastName: string | null;
  category: string | null;
  uciId: string | null;
  raceNumber: string | null;
  race: string | null;
  /** Com'è oggi in elenco questo codice; `null` se il codice è nuovo. */
  existing: ChipComparable | null;
  /** I campi in cui il file dice qualcosa di diverso; solo per `conflict`. */
  differingFields: readonly ChipField[];
};

export type ImportStats = {
  total: number;
  importable: number;
  conflicts: number;
  duplicateInFile: number;
  duplicateInEvent: number;
  missingCode: number;
};

export type ImportAnalysis = {
  rows: ImportRow[];
  stats: ImportStats;
};

/** Converte una cella in testo pulito; `null` se vuota. */
export function cellToString(raw: unknown): string | null {
  if (raw === null || raw === undefined) return null;
  if (raw instanceof Date) return raw.toISOString();
  const text = String(raw).trim();
  return text === '' ? null : text;
}

function readCell(row: readonly unknown[], index: number | null): string | null {
  if (index === null) return null;
  return cellToString(row[index]);
}

/**
 * Analizza le righe di un file già letto, rispetto a una mappatura di colonne.
 *
 * Non importa nulla e non contatta il database: produce l'anteprima che
 * l'operatore conferma. Le righe con codice duplicato — duplicato dentro il file
 * o già presente nell'evento — restano nell'anteprima con il motivo, ma non
 * verranno importate due volte.
 *
 * Un codice già in elenco diventa `conflict` **solo** quando il file porta, per
 * almeno un campo, un valore diverso da quello che c'è: sono le righe su cui
 * l'operatore deve decidere se tenere l'esistente o aggiornarlo. Se il file non
 * aggiunge niente di diverso, la riga resta un semplice `duplicate_in_event` —
 * segnalare un conflitto dove non c'è niente da decidere è solo rumore.
 *
 * @param existingChips i codici già in elenco con la loro anagrafica
 */
export function analyzeImport(
  dataRows: readonly (readonly unknown[])[],
  mapping: ColumnMapping,
  existingChips: ExistingChipMap = new Map(),
): ImportAnalysis {
  const seenInFile = new Set<string>();
  const rows: ImportRow[] = [];
  const stats: ImportStats = {
    total: 0,
    importable: 0,
    conflicts: 0,
    duplicateInFile: 0,
    duplicateInEvent: 0,
    missingCode: 0,
  };

  dataRows.forEach((raw, rowIndex) => {
    const rawCode = readCell(raw, mapping.chipCode) ?? '';
    const code = normalizeChipCode(rawCode);

    const row: ImportRow = {
      rowIndex,
      status: 'ok',
      code,
      rawCode,
      team: readCell(raw, mapping.team),
      firstName: readCell(raw, mapping.firstName),
      lastName: readCell(raw, mapping.lastName),
      category: readCell(raw, mapping.category),
      uciId: readCell(raw, mapping.uciId),
      raceNumber: readCell(raw, mapping.raceNumber),
      race: readCell(raw, mapping.race),
      existing: null,
      differingFields: [],
    };

    // Righe completamente vuote (code e anagrafica assenti): sono la coda di
    // molte esportazioni Excel, non un errore da segnalare.
    if (isEmptyRow(row)) return;

    stats.total += 1;

    if (code === '') {
      row.status = 'missing_code';
      stats.missingCode += 1;
    } else if (seenInFile.has(code)) {
      row.status = 'duplicate_in_file';
      stats.duplicateInFile += 1;
    } else {
      seenInFile.add(code);
      const existing = existingChips.get(code);

      if (existing === undefined) {
        stats.importable += 1;
      } else {
        row.existing = existing;
        row.differingFields = differingFields(row, existing);

        if (row.differingFields.length > 0) {
          row.status = 'conflict';
          stats.conflicts += 1;
        } else {
          row.status = 'duplicate_in_event';
          stats.duplicateInEvent += 1;
        }
      }
    }

    rows.push(row);
  });

  return { rows, stats };
}

/**
 * I campi su cui il file dice qualcosa di diverso da quello che è già in elenco.
 *
 * Una cella vuota nel file non è mai un conflitto: non porta un'informazione
 * diversa, non porta niente. Il confronto ignora maiuscole e spazi ripetuti,
 * perché `"Team  Bianchi"` e `"TEAM BIANCHI"` sono la stessa squadra — e
 * segnalare un conflitto su ogni riga di un file riscritto con altre maiuscole
 * renderebbe l'anteprima inutilizzabile.
 */
function differingFields(row: ImportRow, existing: ChipComparable): ChipField[] {
  return COMPARABLE_FIELDS.filter((field) => {
    const fromFile = row[field];
    if (fromFile === null) return false;
    return compareValue(fromFile) !== compareValue(existing[field]);
  });
}

/** Forma di confronto di un valore anagrafico: senza maiuscole, spazi normalizzati. */
function compareValue(value: string | null): string {
  return (value ?? '').trim().replace(/\s+/g, ' ').toLowerCase();
}

function isEmptyRow(row: ImportRow): boolean {
  return (
    row.rawCode === '' &&
    row.team === null &&
    row.firstName === null &&
    row.lastName === null &&
    row.category === null &&
    row.uciId === null &&
    row.raceNumber === null &&
    row.race === null
  );
}

/** Le sole righe che verranno effettivamente scritte sul database. */
export function importableRows(analysis: ImportAnalysis): ImportRow[] {
  return analysis.rows.filter((row) => row.status === 'ok');
}

export const IMPORT_ROW_STATUS_LABELS: Record<ImportRowStatus, string> = {
  ok: 'Da importare',
  conflict: 'Dati diversi dall’esistente',
  duplicate_in_file: 'Duplicato nel file',
  duplicate_in_event: 'Già presente nell’evento',
  missing_code: 'Senza codice',
};

// ── Riempimento dei campi mancanti ───────────────────────────────────────────

/** I campi che si possono riempire in blocco su un file di soli codici. */
export const FILLABLE_FIELDS = COMPARABLE_FIELDS;
export type FillableField = (typeof FILLABLE_FIELDS)[number];

export const FILLABLE_FIELD_LABELS: Record<FillableField, string> = {
  team: 'Squadra',
  firstName: 'Nome',
  lastName: 'Cognome',
  category: 'Categoria',
  uciId: 'UCI ID',
  raceNumber: 'N. gara',
  race: 'Gara',
};

/**
 * Vero se il file porta dei codici ma nessun dato atleta.
 *
 * È il caso del file di soli transponder: l'operatore deve poter dire se
 * lasciare i campi vuoti (e completarli dopo a mano) oppure assegnare un valore
 * uguale a tutte le righe — una squadra, una gara, un'etichetta.
 */
export function lacksAthleteData(analysis: ImportAnalysis): boolean {
  return analysis.rows.some(
    (row) =>
      row.status === 'ok' &&
      COMPARABLE_FIELDS.every((field) => row[field] === null),
  );
}

/**
 * Riempe i campi vuoti delle righe **in importazione** con un valore unico.
 *
 * Solo i campi vuoti, e solo le righe nuove: un valore del file non viene mai
 * sovrascritto, e le righe già in elenco non vengono toccate — per quelle la
 * decisione è dell'operatore, riga per riga. Restituisce una nuova analisi:
 * l'anteprima è un valore, non uno stato da mutare sotto i piedi di React.
 */
export function applyUniformFill(
  analysis: ImportAnalysis,
  fill: Partial<Record<FillableField, string>>,
): ImportAnalysis {
  const provided = FILLABLE_FIELDS.filter((field) => {
    const value = fill[field];
    return typeof value === 'string' && value.trim() !== '';
  });
  if (provided.length === 0) return analysis;

  return {
    ...analysis,
    rows: analysis.rows.map((row) => {
      if (row.status !== 'ok') return row;
      const filled = { ...row };
      for (const field of provided) {
        if (filled[field] === null) filled[field] = fill[field]!.trim();
      }
      return filled;
    }),
  };
}

// ── Piano di importazione ────────────────────────────────────────────────────

/** Cosa fare di una riga in conflitto: tenere l'esistente o aggiornarlo. */
export type ImportRowDecision = 'keep' | 'update';

/**
 * La scelta prudente è tenere quello che c'è: sovrascrivere i dati di un atleta
 * è l'operazione che si rimpiange, e va fatta apposta.
 */
export const DEFAULT_IMPORT_DECISION: ImportRowDecision = 'keep';

export type ImportPlan = {
  /** Righe da inserire come chip nuovi. */
  inserts: ImportRow[];
  /** Righe già in elenco da aggiornare con i dati del file. */
  updates: ImportRow[];
  /** Righe in conflitto lasciate com'erano. */
  kept: ImportRow[];
};

/**
 * Divide le righe nei tre esiti possibili: nuove, da aggiornare, lasciate.
 *
 * Le righe in conflitto senza una decisione esplicita restano com'erano: il
 * piano non è mai più aggressivo di quello che l'operatore ha chiesto.
 *
 * @param decisions decisione per `rowIndex`; le righe assenti restano invariate
 */
export function planImport(
  analysis: ImportAnalysis,
  decisions: ReadonlyMap<number, ImportRowDecision> = new Map(),
): ImportPlan {
  const inserts: ImportRow[] = [];
  const updates: ImportRow[] = [];
  const kept: ImportRow[] = [];

  for (const row of analysis.rows) {
    if (row.status === 'ok') {
      inserts.push(row);
    } else if (row.status === 'conflict') {
      if (decisions.get(row.rowIndex) === 'update') updates.push(row);
      else kept.push(row);
    }
  }

  return { inserts, updates, kept };
}

/**
 * I soli campi valorizzati dalla riga, nella forma attesa dal server.
 *
 * Per gli aggiornamenti conta: i campi assenti non vengono inviati, e il server
 * non li tocca. Un `null` esplicito non cancellerebbe comunque niente, ma
 * mandarlo sarebbe dire una cosa che non si vuole dire.
 */
export function rowToPayload(row: ImportRow): Record<string, string> {
  const payload: Record<string, string> = {};
  for (const field of COMPARABLE_FIELDS) {
    const value = row[field];
    if (value !== null) payload[field] = value;
  }
  return payload;
}
